import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { catchError, concatMap, map, switchMap, takeUntil, tap, withLatestFrom } from 'rxjs/operators';
import { PageableModel, VentilationParameter } from '@mona/models';
import {
    applyInstancesChanges,
    ChangeLogAction,
    ChangeLogSelectors,
    ChangeLogState,
    getChangesData,
    getPersistedChangesData,
} from '@mona/pdms/data-access-changelog';
import { currentEncounterId, isEmptyEncounterId, withCurrentEncounterId } from '@mona/pdms/data-access-combined';
import { Logger } from '@mona/shared/logger';
import { isSameEncounterFilter } from '@mona/shared/utils';
import { VentilationParametersApi } from '../../infrastructure';
import { VentilationAction } from '../actions';
import { selectVentilationParameters } from '../selectors';

/**
 * Ventilation effects
 */
@Injectable()
export class VentilationEffects {
    private logger = new Logger();
    /**
     * Load ventilation parameters effect
     */
    loadVentilationParameters$ = createEffect(() =>
        this.actions$.pipe(
            ofType(VentilationAction.loadVentilationParameters),
            withCurrentEncounterId(),
            switchMap(([{ params, url }, encounter_id]) =>
                this.ventilationParametersApi.getPageableVentilationParameters({ ...params, encounter_id }, url).pipe(
                    isSameEncounterFilter(currentEncounterId(this.store$), encounter_id),
                    catchError(error => {
                        this.logger.error('Error loading VentilationParameter', error);
                        return of({ results: [], codes: [] } as PageableModel<VentilationParameter>);
                    }),
                    takeUntil(isEmptyEncounterId(this.store$)),
                ),
            ),
            concatLatestFrom(() => this.store$.select(ChangeLogSelectors.getChangesMap)),
            map(([data, changesMap]) => {
                const changes = changesMap['VentilationParameter'] || [];

                if (changes.length) {
                    data.results = applyInstancesChanges<VentilationParameter>(data.results, changes, true);
                }

                return VentilationAction.loadVentilationParametersSuccess(data);
            }),
            catchError(error => of(VentilationAction.loadVentilationParametersFailed({ error }))),
        ),
    );

    /** Listen save change success & add one - realistic update */
    onChangeSaved$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(
                    ChangeLogAction.saveChangeAction.succeededAction,
                    ChangeLogAction.saveChangesAction.succeededAction,
                ),
                withLatestFrom(
                    this.store$.select(selectVentilationParameters),
                    this.store$.select(ChangeLogSelectors.getChangesMap),
                ),
                tap(([, parameters, changesMap]) => {
                    const parameterChanges = changesMap['VentilationParameter'] || [];

                    if (parameterChanges.length) {
                        const data = getChangesData(parameters, parameterChanges);
                        this.store$.dispatch(VentilationAction.upsertVentilationParameters({ data }));
                    }
                }),
            ),
        { dispatch: false },
    );

    /** Listen change persist success & add one - realistic update */
    onPersistChangeSuccess$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(ChangeLogAction.persistChangesAction.succeededAction),
                withLatestFrom(
                    this.store$.select(ChangeLogSelectors.getChangesMap),
                    this.store$.select(selectVentilationParameters),
                ),
                tap(([, changesMap, parameters]) => {
                    const parameterChanges = changesMap['VentilationParameter'] || [];

                    if (parameterChanges.length) {
                        const { toRemove, toUpdate } = getPersistedChangesData(parameters, parameterChanges);

                        this.store$.dispatch(
                            VentilationAction.addParameterChanges({
                                toUpdateEntities: toUpdate || [],
                                toRemoveIds: toRemove.map(({ id }) => id) || [],
                            }),
                        );
                    }
                }),
            ),
        { dispatch: false },
    );

    /**
     * Constructor
     *
     * @param actions$ Actions
     * @param ventilationParametersApi VentilationParametersApi
     * @param store$
     */
    constructor(
        private actions$: Actions,
        private ventilationParametersApi: VentilationParametersApi,
        private store$: Store<{ changelogData: ChangeLogState }>,
    ) {}
}
